import { supabase } from '../supabaseClient';
import type { EvaluationPlanSheetImageScope } from '../types/evaluationPlanSheetCanvas';
import {
  getSheetCanvasPages,
  sheetLayoutSubsetToSlideLayout,
  slideLayoutToSheetLayoutSubset,
  withSheetCanvasPages,
} from '../types/evaluationPlanSheetCanvas';
import { getSheetCanvasFromAttachment } from './evaluationPlanAttachment';
import {
  finalizePendingEvaluationSheetImagesInLayout,
  revokeAllPendingEvaluationSheetInLayout,
} from './evaluationSheetImagePendingUpload';
import { releaseEvaluationSheetImageIfUnreferenced } from '../services/evaluationSheetImageStorageService';
import { notifySlideImageStorageChanged } from '../services/slideImageStorageService';
import type { EvaluationPlanAttachment } from '../types/evaluationPlan';

/**
 * 평가지 캔버스 이미지 수명 주기
 * - 캔버스 [등록]: 새 이미지는 blob(로컬)로만 보관 → 업로드 없음
 * - 평가계획 [저장]: 업로드 후 행 저장
 * - 행 저장 성공 후: 이전 저장 상태엔 있었지만 새 상태엔 없는 이미지 → 서버에서 참조 확인 후 삭제
 */

const SHEET_IMAGE_URL_RE =
  /https?:\/\/[^"'\s\\]+\/storage\/v1\/object\/public\/evaluation-sheet-images\/[^"'\s\\?]+/g;

export function collectEvaluationSheetImageUrls(...values: unknown[]): Set<string> {
  const out = new Set<string>();
  for (const value of values) {
    if (value == null) continue;
    const text = typeof value === 'string' ? value : JSON.stringify(value);
    const matches = text.match(SHEET_IMAGE_URL_RE);
    if (matches) matches.forEach((m) => out.add(m));
  }
  return out;
}

type PlanWithSheet = { evaluation_sheet_attachment?: unknown };

/** 평가계획의 평가지 캔버스에 남은 로컬(blob) 이미지를 업로드하고, 업로드된 URL 목록을 돌려줍니다. */
export async function uploadPendingSheetImagesForPlan<T extends PlanWithSheet>(
  plan: T,
  scope: EvaluationPlanSheetImageScope,
  workspaceId?: string | null
): Promise<{ plan: T; uploadedUrls: string[] }> {
  const attachment = plan.evaluation_sheet_attachment as EvaluationPlanAttachment | null | undefined;
  const sheet = getSheetCanvasFromAttachment(attachment ?? null);
  if (!attachment || !sheet) return { plan, uploadedUrls: [] };
  // 모든 페이지의 로컬 이미지 업로드
  const pages = getSheetCanvasPages(sheet);
  const before = collectEvaluationSheetImageUrls(pages);
  let changed = false;
  const nextPages = [];
  for (const page of pages) {
    const slide = sheetLayoutSubsetToSlideLayout(page);
    const finalized = await finalizePendingEvaluationSheetImagesInLayout({ layout: slide, scope, workspaceId });
    if (finalized === slide) nextPages.push(page);
    else {
      changed = true;
      nextPages.push(slideLayoutToSheetLayoutSubset(finalized));
    }
  }
  if (!changed) return { plan, uploadedUrls: [] };
  const nextSheet = withSheetCanvasPages(sheet, nextPages);
  const uploadedUrls = Array.from(collectEvaluationSheetImageUrls(nextPages)).filter((u) => !before.has(u));
  return {
    plan: {
      ...plan,
      evaluation_sheet_attachment: {
        ...attachment,
        form_data: { ...(attachment.form_data ?? {}), sheet_canvas: nextSheet },
      },
    },
    uploadedUrls,
  };
}

/** 저장하지 않고 닫을 때: 평가계획 초안에 남은 로컬 blob 이미지 해제(메모리) */
export function revokePendingSheetImagesForPlan(plan: PlanWithSheet | null | undefined): void {
  const sheet = getSheetCanvasFromAttachment(
    (plan?.evaluation_sheet_attachment as EvaluationPlanAttachment | null | undefined) ?? null
  );
  if (sheet) getSheetCanvasPages(sheet).forEach((page) => revokeAllPendingEvaluationSheetInLayout(sheetLayoutSubsetToSlideLayout(page)));
}

/**
 * 서버 참조 확인 후 삭제. 서버 함수가 아직 배포되지 않았으면 아무것도 지우지 않습니다(공유 이미지 보호).
 */
export async function releaseEvaluationSheetImagesIfUnreferenced(urls: Iterable<string>): Promise<void> {
  const list = Array.from(new Set(urls));
  if (!list.length) return;
  const { data } = await supabase.auth.getUser();
  const userId = data.user?.id?.trim();
  if (!userId) return;
  let released = false;
  for (const url of list) {
    const result = await releaseEvaluationSheetImageIfUnreferenced(url, userId);
    if (result === 'unsupported') {
      console.info('[평가지 이미지] 참조 확인 함수 미적용 — 삭제를 건너뜁니다.');
      break;
    }
    if (result === 'released') released = true;
  }
  if (released) notifySlideImageStorageChanged();
}

/** 저장 성공 후: 이전 저장 상태(prev)에는 있었지만 새 상태(next)에는 없는 이미지 정리 */
export function reconcileEvaluationSheetImagesAfterSave(prev: unknown, next: unknown): void {
  const keep = collectEvaluationSheetImageUrls(next);
  const removed = Array.from(collectEvaluationSheetImageUrls(prev)).filter((u) => !keep.has(u));
  if (removed.length) void releaseEvaluationSheetImagesIfUnreferenced(removed);
}
